import { CircularProgress, Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { useCallback, useContext, useEffect } from "react";
import { forwardRef } from "react";
import { useState } from "react";
import { useTranslate } from "react-admin";
import { handleAxiosError } from "../../../../common/data/axiosErrorHandler";
import {
  MaintenanceDashboardApiFactory,
  ServiceRemindersDto,
} from "../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { SelectedVehiclesFilterContext, WidgetProps } from "../common";
import { Widget } from "./Widget";

const useStyles = makeStyles((theme: Theme) => ({
  major: {
    color: theme.palette.error.main,
    cursor: "pointer",
    "&:hover": {
      textShadow: `0px 0px 2px ${theme.palette.error.main}, 0 0 1em rgba(255, 255, 255, 0.25), 0 0 0.2em rgba(255, 255, 255, 0.5)`,
    },
  },
  minor: {
    color: theme.palette.warning.main,
    cursor: "pointer",
    "&:hover": {
      textShadow: `0px 0px 2px ${theme.palette.warning.main}, 0 0 1em rgba(255, 255, 255, 0.25), 0 0 0.2em rgba(255, 255, 255, 0.5)`,
    },
  },
}));

export const ServiceReminders = forwardRef(
  ({ className, icon, title, ...rest }: WidgetProps, ref) => {
    const translate = useTranslate();
    const classes = useStyles();
    const [services, setServices] = useState<ServiceRemindersDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const axios = useAxiosInstance();
    const filterContext = useContext(SelectedVehiclesFilterContext);
    useEffect(() => {
      if (!axios) {
        return;
      }

      setLoading(true);
      MaintenanceDashboardApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppMaintenancedashboardServiceremindersGet()
        .then((result) => {
          setServices(result.data);
        })
        .catch((e) => console.error(handleAxiosError(e)))
        .finally(() => setLoading(false));
    }, [axios]);

    const handleOverdueClick = useCallback(() => {
      if (!filterContext || !services?.overdue) {
        return;
      }

      filterContext.setSelectedFilter("WITH_OVERDUE_SERVICES");
    }, [filterContext, services]);

    const handleUpcomingClick = useCallback(() => {
      if (!filterContext || !services?.soon) {
        return;
      }

      filterContext.setSelectedFilter("WITH_UPCOMING_SERVICES");
    }, [filterContext, services]);

    const { overdue, soon } = services ? services : { overdue: 0, soon: 0 };
    return (
      <Widget
        title={title}
        icon={icon}
        className={className}
        ref={ref}
        {...rest}
      >
        <div className="categories">
          {loading ? (
            <div className="category">
              <CircularProgress size="3rem" />
            </div>
          ) : (
            <>
              <div className="category">
                <div
                  className={`category-value ${classes.major}`}
                  onClick={handleOverdueClick}
                >
                  {overdue}
                </div>
                <div className="category-title">
                  {translate("resources.maintenance-dashboard.labels.overdue")}
                </div>
              </div>
              <div className="category">
                <div
                  className={`category-value ${classes.minor}`}
                  onClick={handleUpcomingClick}
                >
                  {soon}
                </div>
                <div className="category-title">
                  {translate("resources.maintenance-dashboard.labels.soon")}
                </div>
              </div>
            </>
          )}
        </div>
      </Widget>
    );
  }
);
